/**
 * Add/Edit Variation dialog (DNKADM-0055 U3) — a MUI `Dialog` that creates/updates a
 * `v2_service_variation` via `createVariation` / `updateVariation` (handoff §5.5).
 *
 * Fields: **name** (e.g. "30 min"), **durationMinutes**, **preBufferMinutes** /
 * **postBufferMinutes** (default 0), **price** (pre-tax money in the tenant currency),
 * **creditCost** (REQUIRED — the save is REJECTED without it; an optional suggested
 * value can be prefilled from the duration but the admin must confirm a number), and
 * **isDefault**.
 *
 * The "exactly one default" invariant is enforced by the parent panel (it counts the
 * existing defaults and, when this dialog sets `isDefault`, the panel clears the others
 * — and it blocks clearing the only default here). A validation `400`/`409` renders
 * inline; anything else a toast (`classifyFormError`).
 *
 * **A length drawer (DNKADM-0493).** On the service page a variation is a "length", and this
 * form opens in the right-side {@link EditDrawer} (one Save, one Cancel) instead of a dialog.
 * Same fields, same validation, same create/update body. A failure of either kind shows
 * inline and the drawer stays open on it; success closes it.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import {
  createVariation,
  updateVariation,
  type ServiceVariation,
  type ServiceVariationInput,
} from '@/api/services';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';
import { EditDrawer } from './EditDrawer';

export interface VariationDialogProps {
  open: boolean;
  serviceId: string;
  /** The variation being edited, or `null` to add a new one. */
  variation: ServiceVariation | null;
  /** The currency code for the price helper text, or null while it loads. */
  currency: string | null;
  /**
   * True when this is the only default variation — clearing `isDefault` is blocked so a
   * service always keeps exactly one default.
   */
  isOnlyDefault: boolean;
  onClose: () => void;
  /** Called with the saved row (or null when the parse degraded) after a write. */
  onSaved: (saved: ServiceVariation | null, wasDefault: boolean) => void;
  showToast: ShowToast;
}

interface FieldErrors {
  name?: string;
  durationMinutes?: string;
  preBufferMinutes?: string;
  postBufferMinutes?: string;
  price?: string;
  noShowFee?: string;
  creditCost?: string;
  isDefault?: string;
}

export function VariationDialog(props: VariationDialogProps) {
  if (!props.open) return null;
  return <VariationDialogForm key={props.variation?.id ?? 'new'} {...props} />;
}

/** Parse a numeric text field to a finite number ≥ 0, or null when invalid/blank. */
function parseNonNegative(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Parse a non-negative integer, or null. */
function parseNonNegativeInt(text: string): number | null {
  const n = parseNonNegative(text);
  return n !== null && Number.isInteger(n) ? n : null;
}

/** Parse an optional non-negative dollar amount with at most two decimal places. */
function parseOptionalDollar(text: string): { valid: true; value: number | null } | { valid: false } {
  const trimmed = text.trim();
  if (trimmed === '') return { valid: true, value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return { valid: false };
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0
    ? { valid: true, value }
    : { valid: false };
}

function VariationDialogForm({
  serviceId,
  variation,
  currency,
  isOnlyDefault,
  onClose,
  onSaved,
  showToast,
}: Omit<VariationDialogProps, 'open'>) {
  const isEdit = variation !== null;

  const [name, setName] = useState(variation?.name ?? '');
  const [durationMinutes, setDurationMinutes] = useState(
    variation ? String(variation.durationMinutes) : '',
  );
  const [preBufferMinutes, setPreBufferMinutes] = useState(
    String(variation?.preBufferMinutes ?? 0),
  );
  const [postBufferMinutes, setPostBufferMinutes] = useState(
    String(variation?.postBufferMinutes ?? 0),
  );
  const initialPrice = variation ? String(variation.price) : '0';
  const [price, setPrice] = useState(initialPrice);
  const [noShowFee, setNoShowFee] = useState(
    variation
      ? variation.noShowFee === null || variation.noShowFee === undefined
        ? variation.price.toFixed(2)
        : variation.noShowFee.toFixed(2)
      : '',
  );
  // creditCost starts EMPTY for a new variation so the admin must confirm a value.
  const [creditCost, setCreditCost] = useState(
    variation ? String(variation.creditCost) : '',
  );
  const [isDefault, setIsDefault] = useState(variation?.isDefault ?? false);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** Suggest a credit cost from the duration (1 credit / 30 min, rounded up, min 1). */
  function suggestCreditCost() {
    const mins = parseNonNegativeInt(durationMinutes);
    const suggestion = mins && mins > 0 ? Math.max(1, Math.ceil(mins / 30)) : 1;
    setCreditCost(String(suggestion));
  }

  function validate(): ServiceVariationInput | null {
    const errors: FieldErrors = {};
    if (name.trim() === '') errors.name = 'Enter a name.';

    const duration = parseNonNegativeInt(durationMinutes);
    if (duration === null || duration <= 0) {
      errors.durationMinutes = 'Enter a duration in minutes (1 or more).';
    }
    const preBuffer = parseNonNegativeInt(preBufferMinutes);
    if (preBuffer === null) errors.preBufferMinutes = 'Enter 0 or more minutes.';
    const postBuffer = parseNonNegativeInt(postBufferMinutes);
    if (postBuffer === null) errors.postBufferMinutes = 'Enter 0 or more minutes.';

    const priceValue = parseNonNegative(price);
    if (priceValue === null) errors.price = 'Enter a price (0 or more).';

    const parsedNoShowFee = parseOptionalDollar(noShowFee);
    if (!parsedNoShowFee.valid) {
      errors.noShowFee = 'Enter 0 or more with up to 2 decimal places.';
    }

    // creditCost is REQUIRED — the save is rejected without it (§5.5).
    const credit = parseNonNegative(creditCost);
    if (credit === null) {
      errors.creditCost = 'Credit cost is required. Enter a number (use Suggest for a starting point).';
    }

    // Keep exactly one default — clearing the only default is blocked.
    if (isEdit && isOnlyDefault && variation?.isDefault && !isDefault) {
      errors.isDefault =
        'A service needs one default length. Make another the default first.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    return {
      name: name.trim(),
      isDefault,
      durationMinutes: duration!,
      preBufferMinutes: preBuffer!,
      postBufferMinutes: postBuffer!,
      price: priceValue!,
      noShowFee: parsedNoShowFee.valid ? parsedNoShowFee.value : null,
      creditCost: credit!,
    };
  }

  async function handleSave(): Promise<boolean> {
    setServerError(null);
    const input = validate();
    if (!input) return false;
    setSaving(true);
    try {
      const saved =
        isEdit && variation
          ? await updateVariation(serviceId, variation.id, input)
          : await createVariation(serviceId, input);
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action — the
      // host's default-variation follow-up only toasts on ITS failure, never on success).
      showToast(isEdit ? 'Length saved' : 'Length added', 'success');
      onSaved(saved, input.isDefault);
      return true;
    } catch (err) {
      const view = classifyFormError(
        err,
        'The length couldn’t be saved. Please try again.',
      );
      // The drawer stays open on a failure, so it is said in the drawer (DNKADM-0493).
      setServerError(view.message);
      return false;
    } finally {
      setSaving(false);
    }
  }

  const currencyAdornment = currency ? (
    <InputAdornment position="start">{currency}</InputAdornment>
  ) : undefined;

  return (
    <EditDrawer open title={isEdit ? 'Edit length' : 'Add length'} onClose={onClose} onSave={handleSave}>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            fullWidth
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name ?? 'e.g. “30 min” or “60 min”.'}
            slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Duration (minutes)"
              type="number"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              disabled={saving}
              size="small"
              required
              error={Boolean(fieldErrors.durationMinutes)}
              helperText={fieldErrors.durationMinutes}
              slotProps={{ htmlInput: { 'aria-label': 'Duration (minutes)', min: 1, step: 1, inputMode: 'numeric' } }}
            />
            <TextField
              label="Price"
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={saving}
              size="small"
              error={Boolean(fieldErrors.price)}
              helperText={fieldErrors.price ?? 'Pre-tax.'}
              slotProps={{
                htmlInput: { 'aria-label': 'Price', min: 0, step: '0.01', inputMode: 'decimal' },
                ...(currencyAdornment ? { input: { startAdornment: currencyAdornment } } : {}),
              }}
            />
          </Stack>

          <TextField
            label="No-Show Fee"
            type="number"
            value={noShowFee}
            onChange={(e) => setNoShowFee(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            error={Boolean(fieldErrors.noShowFee)}
            helperText={
              fieldErrors.noShowFee ??
              'Charged on a no-show. Blank charges the booked price; 0 means no charge.'
            }
            slotProps={{
              htmlInput: {
                'aria-label': 'No-Show Fee',
                min: 0,
                step: '0.01',
                inputMode: 'decimal',
              },
              ...(currencyAdornment ? { input: { startAdornment: currencyAdornment } } : {}),
            }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Pre-buffer (minutes)"
              type="number"
              value={preBufferMinutes}
              onChange={(e) => setPreBufferMinutes(e.target.value)}
              disabled={saving}
              size="small"
              error={Boolean(fieldErrors.preBufferMinutes)}
              helperText={fieldErrors.preBufferMinutes}
              slotProps={{ htmlInput: { 'aria-label': 'Pre-buffer (minutes)', min: 0, step: 1, inputMode: 'numeric' } }}
            />
            <TextField
              label="Post-buffer (minutes)"
              type="number"
              value={postBufferMinutes}
              onChange={(e) => setPostBufferMinutes(e.target.value)}
              disabled={saving}
              size="small"
              error={Boolean(fieldErrors.postBufferMinutes)}
              helperText={fieldErrors.postBufferMinutes}
              slotProps={{ htmlInput: { 'aria-label': 'Post-buffer (minutes)', min: 0, step: 1, inputMode: 'numeric' } }}
            />
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <TextField
              label="Credit cost"
              type="number"
              value={creditCost}
              onChange={(e) => setCreditCost(e.target.value)}
              disabled={saving}
              size="small"
              required
              error={Boolean(fieldErrors.creditCost)}
              helperText={
                fieldErrors.creditCost ??
                'Required — what this length costs in credits. There is no automatic formula.'
              }
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { 'aria-label': 'Credit cost', min: 0, step: '0.01', inputMode: 'decimal' } }}
            />
            <Button
              size="small"
              onClick={suggestCreditCost}
              disabled={saving}
              sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
            >
              Suggest
            </Button>
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { 'aria-label': 'Default length' } }}
              />
            }
            label="Default length"
          />
          {fieldErrors.isDefault && (
            <Alert severity="error" role="alert" sx={{ mt: -1.5 }}>
              {fieldErrors.isDefault}
            </Alert>
          )}

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
    </EditDrawer>
  );
}
